<template>
  <Modal class="max-w-[396px]! tablet-v:max-w-[500px]!" :hide-close="isMobile">
    <Modal.Body>
      <h2 class="text-[24px] font-medium leading-[28px] text-gray-800">
        {{ title }}
      </h2>
      <p
        class="mt-2.5 text-[16px] leading-[22px] text-gray-800 tablet-v:text-[15px]"
      >
        {{ subtitle }}
      </p>

      <div class="mt-[30px]">
        <UpgradeQuantitySelect
          v-if="currentPlan"
          v-model="selectedItemProductId"
          :items="currentPlan.items"
          class="mx-auto w-full max-w-[310px]"
        />
        <Skeleton
          v-else
          class="mx-auto h-[60px] w-full max-w-[310px] rounded-full"
        />
      </div>

      <div
        v-if="currentPlan"
        class="mt-[14px] flex items-center justify-center gap-2"
      >
        <p class="text-[13px] leading-[18px] text-gray-400">
          {{ billingCopy.plain }}
          <span class="font-medium text-gray-600">{{
            billingCopy.action
          }}</span>
        </p>
        <Toggle
          v-model="switchBillingCycle"
          :size="isDesktopLayout ? 'md' : 'lg'"
        />
      </div>
      <Skeleton v-else class="mx-auto mt-[14px] h-5 w-[215px]" />

      <div class="-mx-6 mt-[22px] h-px bg-[#dfdfdf] tablet-v:mx-7" />

      <Alert v-if="showCallout" variant="success" class="mt-[18px]">
        {{ calloutMessage }}
        <button type="button" class="text-link" @click="handleContactUsClick">
          Contact us
        </button>
      </Alert>

      <Alert v-if="showIAPCallout" variant="warning" class="mt-[18px]">
        You've reached the limit {{ platform }} allows for subscriptions. To
        upgrade your plan further, you will need to cancel your subscription on
        {{ platform }} and resubscribe on Dacr.com.
      </Alert>

      <div class="mt-[18px] flex items-center justify-between">
        <div>
          <div class="text-[14px] font-medium leading-[18px] text-gray-400">
            You'll now pay
          </div>
          <div v-if="currentPlan" class="mt-2 flex items-baseline">
            <span class="text-[36px] font-medium leading-[40px] text-gray-800">
              {{ price }}
            </span>
            <span
              class="ml-1 text-[17px] font-medium leading-[17px] text-gray-800"
            >
              /{{ renderedBillingCycle }}
            </span>
          </div>
          <Skeleton v-else class="mt-2 h-[40px] w-[115px]" />
        </div>
        <RouterLink
          to="/change-plans"
          class="text-link text-[16px]"
          @click="close()"
        >
          See plans
        </RouterLink>
      </div>

      <Button
        variant="primary"
        size="lg"
        class="mt-[14px] w-full"
        :disabled="disableButton"
        @click="handleUpgradeClick"
      >
        Upgrade
      </Button>
      <button
        type="button"
        class="mx-auto mt-[14px] block text-link text-[14px] tablet-v:hidden"
        @click="close()"
      >
        Not now
      </button>
    </Modal.Body>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { Modal, Button, Alert, Toggle, Skeleton, useModal } from '@dacr/library'
import {
  PlanItemStatus,
  useMyPlanQueryQuery,
  type Plan,
  type PlanItem,
  type MyPlanQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { SelectPlanType } from '@/shared/types'
import { capitalize } from '@/shared/utils/string'
import { useDevice } from '@/shared/composables/useDevice'
import UpgradeQuantitySelect from './UpgradeQuantitySelect.vue'

const FILES_THRESHOLD = 5000

const props = defineProps<{
  willReachLimit?: boolean
  hasReachedLimit?: boolean
}>()

const { close } = useModal()
const { isMobile, isDesktopLayout } = useDevice()
const router = useRouter()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const { result } = useMyPlanQueryQuery({ enabled: !webviewStore.isActive })

const billingCycle = ref('')
const plans = ref<NonNullable<MyPlanQueryQuery['myPlan']>>()
const selectedItemProductId = ref('')
const selectedItemMaxUnits = ref<number>()

watchEffect(() => {
  plans.value = result.value?.myPlan || billingStore.webviewPlans
  billingCycle.value =
    result.value?.myPlan?.activeBillingCycleType ||
    billingStore.webviewPlans?.activeBillingCycleType ||
    ''
})

watch(selectedItemProductId, () => {
  if (!selectedItemProductId.value || !selectedItem.value) return
  selectedItemMaxUnits.value = selectedItem.value.maxUnits || 0
})

const title = 'Upgrade your limit'

const subtitle = computed(() => {
  if (props.hasReachedLimit) {
    return 'You’ve reached your registration limit. Upgrade your plan to register more work.'
  }
  if (props.willReachLimit) {
    return 'You’re about to reach your registration limit. Upgrade your plan to register more work.'
  }
  return 'Protect more of your work. Upgrade your copyright limit to register even more.'
})

const switchBillingCycle = computed({
  get() {
    return billingCycle.value !== plans.value?.activeBillingCycleType
  },
  set(value) {
    if (!plans.value?.activeBillingCycleType) return
    billingCycle.value = value
      ? plans.value.activeBillingCycleType === 'month'
        ? 'year'
        : 'month'
      : plans.value.activeBillingCycleType
  },
})

const billingCopy = computed(() =>
  billingCycle.value === 'year'
    ? { plain: 'You’re paying yearly.', action: 'Change to monthly.' }
    : { plain: 'You’re paying monthly.', action: 'Change to yearly.' }
)

const platform = computed(() =>
  capitalize(billingStore.billing?.plan.platform || '')
)

const billingCycleGroup = computed(() =>
  plans.value?.groupByBillingCycle.find(
    (billingPeriod) => billingPeriod?.billingCycleType === billingCycle.value
  )
)

const currentPlan = computed(() => {
  // myPlan returns only the subscribed plan; the webview catalog returns every
  // plan, where the bundle is the one with a tier ladder.
  const plans = billingCycleGroup.value?.plans
  const plan = plans?.find((plan) => plan.items.length > 1) || plans?.[0]
  if (!plan) return
  const items = plan.items.filter(
    (item, index) =>
      item.status === PlanItemStatus.Active || index === plan.items.length - 1
  )
  return { ...plan, items }
})

const selectedItem = computed(() =>
  currentPlan.value?.items.find(
    (item) => item.productId === selectedItemProductId.value
  )
)

const lastItem = computed(
  () => currentPlan.value?.items[currentPlan.value.items.length - 1]
)

const nextAvailablePlanItem = computed(() => {
  const lastItemMaxUnits = lastItem.value?.maxUnits || 0

  if (selectedItemMaxUnits.value) {
    const maxUnits = Math.min(selectedItemMaxUnits.value || 0, lastItemMaxUnits)
    return currentPlan.value?.items.find(
      (item) => (item.maxUnits || 0) >= maxUnits
    )
  }

  if (lastItemMaxUnits <= (userStore.user?.totalFilesLimit || 0)) {
    return lastItem.value
  }

  const maxUnits = Math.min(
    userStore.user?.totalFilesLimit || 0,
    lastItemMaxUnits
  )
  return currentPlan.value?.items.find(
    (item) => (item.maxUnits || 0) > maxUnits
  )
})

watch(
  billingCycle,
  () => {
    selectedItemProductId.value = nextAvailablePlanItem.value?.productId || ''
  },
  { immediate: true }
)

const disableButton = computed(() => {
  if (!currentPlan.value) return true
  if (
    lastItem.value?.productId === selectedItemProductId.value &&
    billingStore.billing?.plan.id === lastItem.value.productId
  ) {
    return true
  }
  return false
})

const price = computed(
  () => selectedItem.value?.prices[0].details?.renderedPrice
)

const renderedBillingCycle = computed(
  () =>
    selectedItem.value?.prices[0].details?.billingCycleType ||
    billingCycle.value
)

const showCallout = computed(
  () =>
    !webviewStore.isActive &&
    ((selectedItem.value?.maxUnits || 0) >= FILES_THRESHOLD ||
      selectedItemProductId.value ===
        currentPlan.value?.items.at(-1)?.productId)
)

const calloutMessage = computed(() => {
  if (
    selectedItemProductId.value === currentPlan.value?.items.at(-1)?.productId
  ) {
    return 'Increase your limit and save big!'
  }
  if ((selectedItem.value?.maxUnits || 0) >= FILES_THRESHOLD) {
    return 'Save big with higher limits!'
  }
  return ''
})

const showIAPCallout = computed(
  () =>
    webviewStore.isActive &&
    currentPlan.value?.items.at(-1)?.productId === selectedItemProductId.value
)

const handleContactUsClick = () => {
  if (webviewStore.isActive) {
    return webviewStore.postMessage({ type: NativeActionsEnum.ContactUs })
  }
  window.open('https://help.dacr.com/hc/en-us/requests/new', '_blank')
}

const handleUpgradeClick = () => {
  if (webviewStore.isActive) {
    webviewStore.postMessage({
      type: NativeActionsEnum.PlanSelected,
      data: {
        productId: selectedItem.value?.nativeProductId,
        planId: selectedItem.value?.nativePlanId,
      },
    })
    return close()
  }

  if (!billingStore.canUpdateSubscription) return

  billingStore.setPlanData({
    plan: currentPlan.value as Plan,
    planItem: selectedItem.value as PlanItem,
    billingCycle: {
      type: billingCycleGroup.value?.billingCycleType || '',
      description: billingCycleGroup.value?.billingCycleDescription || '',
    },
  })

  router.push(`/${SelectPlanType.Change}/checkout`)
  close()
}
</script>
